import { Injectable } from "@angular/core";
import { Utils } from "../resources/utils";
import { ConsignmentCargo } from "../interfaces/consignmentCargo";
import { Cargo } from "../interfaces/cargo";
import { DateManager } from "./date.manager";
import { ConsignmentCityOption } from "src/app/shared/consignment-card/consignment-card.component";
import { Origin } from "../interfaces/origin";
import { Destination } from "../interfaces/destination";
import { AddressCargo, AddressContainer } from "../interfaces/addressCargo";
import { FormBuilder, FormGroup, ValidatorFn, Validators } from "@angular/forms";
import { Patterns } from "../resources/patterns";

export interface ConsignmentDetailField {
    label: string;
    value: string;
    auxiliaryValue?: string;
    show: boolean;
    edit?: { isEditable: boolean, modificationCode: string };
}

@Injectable()
export class ConsignmentManager {
    private cargoDateFormats = {
        dateLoad: 'YYYY-MM-DD HH:mm ZZ',
        downloadDate: 'YYYY-MM-DD HH:mm ZZ',
        time: 'HH:mm'
    };
    private consignmentDateFormats = {
        date: 'DD/MM/YYYY ZZ',
        appointmentTime: 'HH:mm'
    };
    constructor(
        public utils: Utils,
        private formBuilder: FormBuilder,
        private patterns: Patterns
    ) { }

    getContainerValidators(cargo: Cargo): ValidatorFn[] {
        const isContainerService = this.utils.getNestedValue(cargo, 'cargoModel.cargoType.name') === 'Contenedor';
        return isContainerService
            ? [Validators.required, Validators.pattern(this.patterns.CONTAINER_FORMAT_FULL.source)]
            : [];
    }

    createConsignmentForm(cargo: Cargo): FormGroup {
        const containerValidators = this.getContainerValidators(cargo);
        return this.formBuilder.group({
            id: [''],
            state: [''],
            entryId: [''],
            cargoId: [cargo && cargo.id ? cargo.id : ''],
            approvalId: [''],
            documentId: [''],
            approvalInitialId: [''],
            consecutiveCargo: [cargo && this.utils.isDefined(cargo.consecutive) ? cargo.consecutive : ''],
            transportCompanyId: [''],
            operationType: this.formBuilder.group({
                code: ['', Validators.required],
                description: ['', Validators.required]
            }),
            packagingType: this.formBuilder.group({
                code: ['', Validators.required],
                description: ['', Validators.required]
            }),
            cargoNature: this.formBuilder.group({
                code: ['', Validators.required],
                description: ['', Validators.required]
            }),
            merchandise: this.formBuilder.group({
                code: ['', Validators.required],
                description: ['', Validators.required]
            }),
            container: ['', containerValidators],
            chapter: [''],
            section: [''],
            requiresSubSection: [false],
            subSection: [''],
            requiresTaxCode: [false],
            tax: [''],
            isDangerous: [false],
            isHazardousWaste: [false],
            dangerousMerchandise: this.formBuilder.group({
                unProduct: [''],
                merchandiseState: [''],
                hasPackaging: [false],
                packaging: [''],
                nepName: [''],
            }),
            hazardousWaste: this.formBuilder.group({
                hazardousWasteDescription: [''],
                waste: [''],
                wasteDisaggregation: [''],
                hazardLevel: [''],
                hasWasteDisaggregation: [false],
            }),
            cargoOwner: this.formBuilder.group({
                documentType: [cargo && cargo.cargoOwner && cargo.cargoOwner.documentType ? cargo.cargoOwner.documentType : '', Validators.required],
                documentNumber: [cargo && cargo.cargoOwner && cargo.cargoOwner.documentNumber ? cargo.cargoOwner.documentNumber : '', Validators.required],
                name: [cargo && cargo.cargoOwner && cargo.cargoOwner.name ? cargo.cargoOwner.name : '', Validators.required],
                branchOffice: [cargo && cargo.cargoOwner && cargo.cargoOwner.branchOffice ? cargo.cargoOwner.branchOffice : '', Validators.required],
            }),
            policyHolder: [''],
            policyNumber: [''],
            policyExpiryDate: [''],
            insurerDocNumber: [''],
            load: this.formBuilder.group({
                date: ['', Validators.required],
                appointmentTime: ['', Validators.required],
                hours: [1, Validators.required],
                minutes: [0, Validators.required]
            }),
            emptyContainerWeight: [''],
            sender: this.formBuilder.group({
                documentType: ['', Validators.required],
                documentNumber: ['', Validators.required],
                name: ['', Validators.required],
                branchOffice: [''],
                address: ['', Validators.required],
                addressId: ['', Validators.required],
                city: ['', Validators.required],
                cityId: ['', Validators.required],
                municipalityCode: ['', Validators.required],
                location: this.formBuilder.group({
                    lat: [0],
                    lng: [0]
                }),
                fromDestination: [false]
            }),
            totalWeight: [0, [Validators.min(0), Validators.required]],
            weightUnit: ['1', Validators.required],
            amountProduct: [1],
            unitMeasureProduct: ['1'],
            productDescription: [''],
            recipient: this.formBuilder.group({
                documentType: ['', Validators.required],
                documentNumber: ['', Validators.required],
                name: ['', Validators.required],
                branchOffice: [''],
                address: ['', Validators.required],
                addressId: ['', Validators.required],
                city: ['', Validators.required],
                cityId: ['', Validators.required],
                municipalityCode: ['', Validators.required],
                location: this.formBuilder.group({
                    lat: [0],
                    lng: [0]
                })
            }),
            unload: this.formBuilder.group({
                date: ['', Validators.required],
                appointmentTime: ['', Validators.required],
                hours: [1, Validators.required],
                minutes: [0, Validators.required]
            }),
            errorCreation: [''],
            errorInitialFulfillment: [''],
            errorFinalFulfillment: [''],
            errorCancellation: [''],
            consolidationType: this.formBuilder.group({
                id: ['', Validators.required],
                name: ['', Validators.required]
            })
        });
    }

    consignmentHasDangerousNature(consignment: ConsignmentCargo): boolean {
        const cargoNatureCode = this.utils.getNestedValue(consignment, 'cargoNature.code');
        return ['2', '5'].includes(cargoNatureCode);
    }
    consignmentHasHazardousWaste(consignment: ConsignmentCargo): boolean {
        const cargoNatureCode = this.utils.getNestedValue(consignment, 'cargoNature.code');
        return cargoNatureCode === '5';
    }
    consignmentRequiresDescription(consignment: ConsignmentCargo): boolean {
        const operationTypeCode = this.utils.getNestedValue(consignment, 'operationType.code');
        if (operationTypeCode === 'V') return false;
        const cargoNatureCode = this.utils.getNestedValue(consignment, 'cargoNature.code');
        return !['2', '5'].includes(cargoNatureCode);
    }

    consignmentRequiresConsolidationType(consignment: ConsignmentCargo): boolean {
        const operationTypeCode = this.utils.getNestedValue(consignment, 'operationType.code');
        return operationTypeCode === 'P';
    }

    consignmentRequiresAmountProduct(consignment: ConsignmentCargo): boolean {
        const operationTypeCode = this.utils.getNestedValue(consignment, 'operationType.code');
        return !this.isUrbanConsignment(consignment) && operationTypeCode !== 'V';
    }
    isUrbanConsignment(consignment: ConsignmentCargo): boolean {
        const senderMunicipalityCode = this.utils.getNestedValue(consignment, 'sender.municipalityCode');
        const recipientMunicipalityCode = this.utils.getNestedValue(consignment, 'recipient.municipalityCode');
        return senderMunicipalityCode && recipientMunicipalityCode && senderMunicipalityCode === recipientMunicipalityCode;
    }

    getCompleteDangerousFields(consignment: ConsignmentCargo): { valid: boolean, error?: string } {
        //Validaciones de mercancías peligrosas
        if (!consignment.isDangerous && !this.consignmentHasDangerousNature(consignment)) return { valid: true };
        const UNCode = this.utils.getNestedValue(consignment, 'dangerousMerchandise.unProduct.code');
        const UNCodeDescription = this.utils.getNestedValue(consignment, 'dangerousMerchandise.unProduct.description');
        if (!UNCode || !UNCodeDescription) return { valid: false, error: 'Se requiere diligenciar un código UN válido' };
        const productTypeStateValid = this.utils.getNestedValue(consignment, 'dangerousMerchandise.merchandiseState.code')
            && this.utils.getNestedValue(consignment, 'dangerousMerchandise.merchandiseState.description');
        if (!productTypeStateValid) return { valid: false, error: 'Se requiere diligenciar el estado del producto' };
        const hasPackaging = this.utils.getNestedValue(consignment, 'dangerousMerchandise.hasPackaging');
        const packagingValid = this.utils.getNestedValue(consignment, 'dangerousMerchandise.packaging');
        if (hasPackaging && !packagingValid) return { valid: false, error: 'Se requiere diligenciar el grupo de embalaje' };
        const chemicalGroupNEPValid = this.utils.getNestedValue(consignment, 'dangerousMerchandise.nepName');
        if (UNCodeDescription.includes('N.E.P.') && !chemicalGroupNEPValid) return { valid: false, error: 'Se requiere diligenciar el grupo químico N.E.P.' };
        //Validaciones de residuos peligrosos
        if (!consignment.isHazardousWaste && !this.consignmentHasHazardousWaste(consignment)) return { valid: true };
        const hazardousDescription = this.utils.getNestedValue(consignment, 'hazardousWaste.hazardousWasteDescription');
        if (!hazardousDescription) return { valid: false, error: 'Se requiere diligenciar la descripción del residuo peligroso' };
        const hazardousLevel = this.utils.getNestedValue(consignment, 'hazardousWaste.hazardLevel');
        if (!hazardousLevel) return { valid: false, error: 'Se requiere diligenciar la característica de peligrosidad' };
        const hazardousWasteCode = this.utils.getNestedValue(consignment, 'hazardousWaste.waste.code');
        const hazardousWasteDescription = this.utils.getNestedValue(consignment, 'hazardousWaste.waste.description');
        if (!hazardousWasteCode || !hazardousWasteDescription) return { valid: false, error: 'Se requiere diligenciar la corriente de residuos peligrosos' };
        const hasWasteDisaggregation = this.utils.getNestedValue(consignment, 'hazardousWaste.hasWasteDisaggregation');
        const disaggregationValid = this.utils.getNestedValue(consignment, 'hazardousWaste.wasteDisaggregation.code') && this.utils.getNestedValue(consignment, 'hazardousWaste.wasteDisaggregation.description');
        if (hasWasteDisaggregation && !disaggregationValid) return { valid: false, error: 'Se requiere diligenciar la desagregación' };
        return { valid: true };
    }

    getCompleteVariableFields(consignment: ConsignmentCargo, cargo?: Cargo): { valid: boolean, error?: string } {
        const productDescription = this.utils.getNestedValue(consignment, 'productDescription');
        if (this.consignmentRequiresDescription(consignment) && !productDescription)
            return { valid: false, error: 'Se requiere diligenciar la descripción de la mercancía' };
        if (this.consignmentRequiresDescription(consignment) && productDescription.length > 60)
            return { valid: false, error: 'La descripción de la mercancía no puede tener más de 60 caracteres' };
        const consolidationType = this.utils.getNestedValue(consignment, 'consolidationType');
        if (this.consignmentRequiresConsolidationType(consignment) && (!consolidationType || !consolidationType.id || !consolidationType.name))
            return { valid: false, error: 'Se requiere diligenciar el tipo de consolidación de la remesa' };
        if (consignment.requiresSubSection && !this.utils.getNestedValue(consignment, 'subSection.code'))
            return { valid: false, error: 'Se requiere diligenciar la subpartida' };
        if (consignment.requiresTaxCode && !this.utils.getNestedValue(consignment, 'tax.code'))
            return { valid: false, error: 'Se requiere diligenciar el código de arancel' };
        const amountProduct = this.utils.getNestedValue(consignment, 'amountProduct');
        if (this.consignmentRequiresAmountProduct(consignment) && !amountProduct)
            return { valid: false, error: 'Se requiere diligenciar la cantidad de medida del producto' };
        const unitMeasureProduct = this.utils.getNestedValue(consignment, 'unitMeasureProduct.code');
        if (this.consignmentRequiresAmountProduct(consignment) && !unitMeasureProduct)
            return { valid: false, error: 'Se requiere diligenciar la unidad de medida del producto' };
        const totalWeight = Number(consignment.totalWeight);
        const operationCode = this.utils.getNestedValue(consignment, 'operationType.code');
        if (totalWeight <= 0 && operationCode !== 'V')
            return { valid: false, error: 'La cantidad cargada debe ser mayor a 0 para remesas distintas a contenedores vacíos' };
        else if (totalWeight > 0 && operationCode === 'V')
            return { valid: false, error: 'La cantidad cargada debe ser 0 para contenedores vacíos' };
        const isContainerService = cargo
            && this.utils.getNestedValue(cargo, 'cargoModel.cargoType.name') === 'Contenedor';
        if (isContainerService && !consignment.container)
            return { valid: false, error: 'Se requiere diligenciar el número de contenedor' };
        return { valid: true };
    }

    getAlerts(cargo: Cargo, cargoConsignment: ConsignmentCargo, indexDestination: number, indexAddress: number): string | null {
        if (!cargo || !cargoConsignment || !cargoConsignment.id) return 'No es posible validar la remesa';
        // Errores RNDC no bloquean si la remesa fue autorizada bajo contingencia sin transmitir al ministerio
        const isAuthorizedUnderContingency = cargoConsignment.authorizedWithoutConsignment && !cargoConsignment.entryId;
        if (cargoConsignment.errorCreation && !isAuthorizedUnderContingency) return cargoConsignment.errorCreation;
        if (cargoConsignment.errorInitialFulfillment) return cargoConsignment.errorInitialFulfillment;
        if (cargoConsignment.errorFinalFulfillment) return cargoConsignment.errorFinalFulfillment;
        if (cargoConsignment.errorCancellation) return cargoConsignment.errorCancellation;

        const cargoOrigin: Origin = this.utils.getNestedValue(cargo, 'cargoFeature.uploadDownload.origin');
        const cargoOriginAddresses: AddressCargo[] = this.utils.getNestedValue(cargoOrigin, 'addresses');
        if (!cargoOriginAddresses || !cargoOriginAddresses.length)
            return 'No hay direcciones de origen registradas';

        const cargoDestination: Destination = this.utils.getNestedValue(cargo, `cargoFeature.uploadDownload.destination.${indexDestination}`);
        if (!cargoDestination)
            return 'No hay destino registrado';

        const cargoDestinationAddress: AddressCargo = this.utils.getNestedValue(cargoDestination, `addresses.${indexAddress}`);
        if (!cargoDestinationAddress)
            return 'No hay dirección de destino registrada';

        const consignmentAddress: string = this.utils.getNestedValue(cargoConsignment, 'sender.address');
        const cargoOriginAddress: AddressCargo = cargoOriginAddresses.find(address => address && address.address === consignmentAddress);
        if (!cargoOriginAddress)
            return 'La dirección de origen de la remesa no coincide con ninguna dirección de origen del servicio';

        const consignmentRecipientCity: string = this.utils.getNestedValue(cargoConsignment, 'recipient.municipalityCode');
        if (consignmentRecipientCity !== cargoDestination.municipalityCode)
            return 'La ciudad de destino de la remesa no coincide con ninguna ciudad de destino del servicio';

        const consignmentRecipientAddress: string = this.utils.getNestedValue(cargoConsignment, 'recipient.address');
        if (consignmentRecipientAddress !== cargoDestinationAddress.address)
            return 'La dirección de destino de la remesa no coincide con ninguna dirección de destino del servicio';

        const isContainerService = this.utils.getNestedValue(cargo, 'cargoModel.cargoType.name') === 'Contenedor';
        if (isContainerService && cargoConsignment.container) {
            const cargoDestinationContainers: AddressContainer[] = this.utils.getNestedValue(cargoDestinationAddress, 'containers') || [];
            const matchedContainer = cargoDestinationContainers.find(addressContainer => addressContainer && addressContainer.consignmentId === cargoConsignment.id);
            if (!matchedContainer || matchedContainer.container !== cargoConsignment.container)
                return 'El contenedor de la remesa no coincide con el contenedor del servicio';
        }

        const consignmentLoadDate: string = this.utils.getNestedValue(cargoConsignment, 'load.date');
        const cargoDateLoad: string = this.utils.getNestedValue(cargo, 'dateLoad');
        if (consignmentLoadDate && cargoDateLoad) {
            const consignmentLoadDateTime: Date = DateManager.stringToDate(consignmentLoadDate, this.consignmentDateFormats.date);
            const cargoLoadDateTime: Date = DateManager.stringToDate(cargoDateLoad, this.cargoDateFormats.dateLoad);
            if (!DateManager.isSame(consignmentLoadDateTime, cargoLoadDateTime))
                return 'La fecha de cargue de la remesa no coincide con la fecha de cargue del servicio';
        }
        const consignmentUnloadDate: string = this.utils.getNestedValue(cargoConsignment, 'unload.date');
        if (consignmentUnloadDate && cargoDestination.downloadDate) {
            const consignmentUnloadDateTime: Date = DateManager.stringToDate(consignmentUnloadDate, this.consignmentDateFormats.date);
            const cargoUnloadDateTime: Date = DateManager.stringToDate(cargoDestination.downloadDate, this.cargoDateFormats.downloadDate);
            if (!DateManager.isSame(consignmentUnloadDateTime, cargoUnloadDateTime))
                return 'La fecha de descargue de la remesa no coincide con la fecha de descargue del servicio';
        }
        if (this.utils.getNestedValue(cargo, 'cargoOwner.documentNumber') !== this.utils.getNestedValue(cargoConsignment, 'cargoOwner.documentNumber'))
            return 'El documento del propietario de la remesa no coincide con el documento del propietario del servicio';
        return null;
    }

    /**
     * Obtiene los campos de detalle de consignación para renderizar dinámicamente
     * @param consignment Índice de la consignación
     * @returns Array de objetos con la configuración de cada campo
     */
    getConsignmentDetailFields(cargo: Cargo, cargoConsignment: ConsignmentCargo, indexDestination: number, indexAddress: number): ConsignmentDetailField[] {
        if (!cargo || !cargoConsignment) return [];
        const fields: ConsignmentDetailField[] = [
            {
                label: 'Tipo de remesa',
                value: this.utils.getNestedValue(cargoConsignment, 'operationType.description'),
                show: true
            },
            {
                label: 'N° de contenedor',
                value: this.utils.getNestedValue(cargoConsignment, 'container'),
                show: !!this.utils.getNestedValue(cargoConsignment, 'container')
            },
            {
                label: 'Tipo de consolidación',
                value: this.utils.getNestedValue(cargoConsignment, 'consolidationType.name'),
                show: !!this.utils.getNestedValue(cargoConsignment, 'consolidationType.name')
            },
            {
                label: 'Naturaleza de la carga',
                value: this.utils.getNestedValue(cargoConsignment, 'cargoNature.description'),
                show: true
            },
            {
                label: 'Nombre del propietario',
                value: this.utils.getNestedValue(cargoConsignment, 'cargoOwner.name') + ' (' + this.utils.getNestedValue(cargoConsignment, 'cargoOwner.documentNumber') + ')',
                show: true,
                edit: {
                    isEditable: this.utils.getNestedValue(cargoConsignment, 'cargoOwner.documentNumber') !== this.utils.getNestedValue(cargo, 'cargoOwner.documentNumber'),
                    modificationCode: '4'
                }
            },
            {
                label: 'Código de sede del propietario',
                value: this.utils.getNestedValue(cargoConsignment, 'cargoOwner.branchOffice'),
                show: true,
                edit: {
                    isEditable: this.utils.getNestedValue(cargoConsignment, 'cargoOwner.branchOffice') !== this.utils.getNestedValue(cargo, 'cargoOwner.branchOffice'),
                    modificationCode: '4'
                }
            },
            {
                label: 'Remitente',
                value: this.utils.getNestedValue(cargoConsignment, 'sender.name'),
                auxiliaryValue: this.utils.getNestedValue(cargo, 'cargoOwner.name'),
                show: true
            },
            {
                label: 'Dirección de origen',
                value: this.utils.getNestedValue(cargoConsignment, 'sender.branchOffice') ? `(${cargoConsignment.sender.branchOffice}) ${this.utils.getNestedValue(cargoConsignment, 'sender.address')}` : this.utils.getNestedValue(cargoConsignment, 'sender.address'),
                auxiliaryValue: this.utils.getNestedValue(cargo, 'cargoFeature.uploadDownload.origin.addresses.0.address'),
                show: true
            },
            {
                label: 'Fecha y hora de cargue',
                value: this.getDateTime(cargoConsignment, cargo, 'load', 'dateLoad', 'origin.addresses.0'),
                show: true,
                edit: { isEditable: !!(cargo.manifestAuthorization && cargo.manifest), modificationCode: '1' }
            },
            {
                label: 'Tiempo pactado de cargue',
                value: this.getTime(cargoConsignment, cargo, 'load', 'origin.addresses.0'),
                show: true
            },
            {
                label: 'Destinatario',
                value: this.utils.getNestedValue(cargoConsignment, 'recipient.name'),
                auxiliaryValue: this.utils.getNestedValue(cargo, `cargoFeature.uploadDownload.destination.${indexDestination}.addresses.${indexAddress}.thirdPartyConsignment.name`),
                show: true
            },
            {
                label: 'Dirección de destino',
                value: this.utils.getNestedValue(cargoConsignment, 'recipient.branchOffice') ? `(${cargoConsignment.recipient.branchOffice}) ${this.utils.getNestedValue(cargoConsignment, 'recipient.address')}` : this.utils.getNestedValue(cargoConsignment, 'recipient.address'),
                auxiliaryValue: this.utils.getNestedValue(cargo, `cargoFeature.uploadDownload.destination.${indexDestination}.addresses.${indexAddress}.address`),
                show: true,
                edit: { isEditable: false /*!!(cargo.manifestAuthorization && cargo.manifest)*/, modificationCode: '3' }
            },
            {
                label: 'Fecha y hora de descargue',
                value: this.getDateTime(cargoConsignment, cargo, 'unload', 'downloadDate', `destination.${indexDestination}.addresses.${indexAddress}`),
                show: true,
                edit: { isEditable: !!(cargo.manifestAuthorization && cargo.manifest), modificationCode: '2' }
            },
            {
                label: 'Tiempo pactado de descargue',
                value: this.getTime(cargoConsignment, cargo, 'unload', `destination.${indexDestination}.addresses.${indexAddress}`),
                show: true
            },
            {
                label: 'Tipo de mercancía (Partida)',
                value: this.utils.getNestedValue(cargoConsignment, 'merchandise.description'),
                auxiliaryValue: this.utils.getNestedValue(cargo, 'cargoFeature.productType.name'),
                show: true
            },
            {
                label: 'Tipo de empaque',
                value: this.utils.getNestedValue(cargoConsignment, 'packagingType.description'),
                auxiliaryValue: this.utils.getNestedValue(cargo, 'cargoModel.packagingType.description'),
                show: true
            },
            {
                label: 'Cantidad cargada',
                value: this.getTotalWeight(cargoConsignment, cargo, indexDestination, indexAddress) + ' kg',
                show: true
            },
            {
                label: 'Descripción de la mercancía',
                value: this.utils.getNestedValue(cargoConsignment, 'productDescription'),
                show: !!this.utils.getNestedValue(cargoConsignment, 'productDescription')
            },
            {
                label: 'Subpartida',
                value: this.utils.getNestedValue(cargoConsignment, 'subSection.description'),
                show: !!this.utils.getNestedValue(cargoConsignment, 'subSection.description')
            },
            {
                label: 'Arancel',
                value: this.utils.getNestedValue(cargoConsignment, 'tax.description'),
                show: !!this.utils.getNestedValue(cargoConsignment, 'tax.description')
            },
            {
                label: 'Cantidad de medida del producto',
                value: this.getProductAmount(cargoConsignment),
                show: !!this.utils.getNestedValue(cargoConsignment, 'amountProduct')
            },
            {
                label: 'Código UN',
                value: this.getUnCode(cargoConsignment),
                show: !!this.utils.getNestedValue(cargoConsignment, 'dangerousMerchandise.unProduct.code')
            },
            {
                label: 'Estado del producto',
                value: this.utils.getNestedValue(cargoConsignment, 'dangerousMerchandise.merchandiseState.description'),
                show: !!this.utils.getNestedValue(cargoConsignment, 'dangerousMerchandise.merchandiseState.description')
            },
            {
                label: 'Grupo de embalaje',
                value: this.utils.getNestedValue(cargoConsignment, 'dangerousMerchandise.packaging'),
                show: !!this.utils.getNestedValue(cargoConsignment, 'dangerousMerchandise.packaging')
            },
            {
                label: 'Grupo químico N.E.P.',
                value: this.utils.getNestedValue(cargoConsignment, 'dangerousMerchandise.nepName'),
                show: !!this.utils.getNestedValue(cargoConsignment, 'dangerousMerchandise.nepName')
            },
            {
                label: 'Descripción del residuo peligroso',
                value: this.utils.getNestedValue(cargoConsignment, 'hazardousWaste.hazardousWasteDescription'),
                show: !!this.utils.getNestedValue(cargoConsignment, 'hazardousWaste.hazardousWasteDescription')
            },
            {
                label: 'Característica de peligrosidad',
                value: this.utils.getNestedValue(cargoConsignment, 'hazardousWaste.hazardLevel.description'),
                show: !!this.utils.getNestedValue(cargoConsignment, 'hazardousWaste.hazardLevel.description')
            },
            {
                label: 'Corriente de residuos peligrosos',
                value: this.utils.getNestedValue(cargoConsignment, 'hazardousWaste.waste.description'),
                show: !!this.utils.getNestedValue(cargoConsignment, 'hazardousWaste.waste.description')
            },
            {
                label: 'Desagregación de residuos peligrosos',
                value: this.utils.getNestedValue(cargoConsignment, 'hazardousWaste.wasteDisaggregation.description'),
                show: !!this.utils.getNestedValue(cargoConsignment, 'hazardousWaste.wasteDisaggregation.description')
            }
        ];

        return fields.filter(field => field.show);
    }

    /**
     * Obtiene la fecha y hora de cargue o descargue formateada
     */
    private getDateTime(cargoConsignment: ConsignmentCargo, cargo: Cargo, type: 'load' | 'unload', dateCargo: string, address: string): string {
        const cargoDate: string = this.utils.getNestedValue(cargo, dateCargo);
        const consignmentDate: string = this.utils.getNestedValue(cargoConsignment, `${type}.date`);
        const date = consignmentDate
            ? DateManager.formatDate(consignmentDate, this.consignmentDateFormats.date, 'YYYY-MM-DD')
            : cargoDate
                ? DateManager.formatDate(cargoDate, this.cargoDateFormats.dateLoad, 'YYYY-MM-DD')
                : null;

        const consignmentTime: string = this.utils.getNestedValue(cargoConsignment, `${type}.appointmentTime`);
        const cargoTime: string = this.utils.getNestedValue(cargo, `cargoFeature.uploadDownload.${address}.time`);
        const time = consignmentTime ? consignmentTime : cargoTime ? cargoTime : null;

        return `${date ? date : ''} ${time ? time : ''}`;
    }

    /**
     * Obtiene el tiempo pactado de cargue o descargue formateado
     */
    private getTime(cargoConsignment: ConsignmentCargo, cargo: Cargo, type: 'load' | 'unload', address: string): string {
        const consignmentHours: string = this.utils.getNestedValue(cargoConsignment, `${type}.hours`);
        const cargoHours: string = this.utils.getNestedValue(cargo, `cargoFeature.uploadDownload.${address}.timePact`);
        const hours = consignmentHours ? consignmentHours : cargoHours ? cargoHours : null;

        const consignmentMinutes: string = this.utils.getNestedValue(cargoConsignment, `${type}.minutes`);
        const cargoMinutes: string = this.utils.getNestedValue(cargo, `cargoFeature.uploadDownload.${address}.minutePact`);
        const minutes = consignmentMinutes ? consignmentMinutes : cargoMinutes ? cargoMinutes : null;

        let result = '';
        if (this.utils.isDefined(hours) && Number(hours) !== 0)
            result += `${hours} horas `;
        if (this.utils.isDefined(minutes) && Number(minutes) !== 0)
            result += `${minutes} minutos`;
        return result.trim() || '';
    }
    /**
     * Obtiene el peso total formateado
     */
    private getTotalWeight(cargoConsignment: ConsignmentCargo, cargo: Cargo, indexDestination: number, indexAddress: number): string {
        const totalWeight = this.utils.getNestedValue(cargoConsignment, 'totalWeight')
        if (totalWeight) return totalWeight.toString();
        const totalWeightCargo = this.utils.getNestedValue(cargo, `cargoFeature.uploadDownload.destination.${indexDestination}.addresses.${indexAddress}.cargoMeasure.totalWeigth`)
        if (totalWeightCargo) return totalWeightCargo.toString();
        return '0';
    }

    /**
     * Obtiene la cantidad de medida del producto formateada
     */
    private getProductAmount(cargoConsignment: ConsignmentCargo): string {
        const amount = this.utils.getNestedValue(cargoConsignment, 'amountProduct');
        const unit = this.utils.getNestedValue(cargoConsignment, 'unitMeasureProduct.description');
        return `${amount}${unit ? ' ' + unit : ''}`;
    }

    /**
     * Obtiene el código UN formateado
     */
    private getUnCode(cargoConsignment: ConsignmentCargo): string {
        const code = this.utils.getNestedValue(cargoConsignment, 'dangerousMerchandise.unProduct.code');
        const description = this.utils.getNestedValue(cargoConsignment, 'dangerousMerchandise.unProduct.description');
        return `${code}. ${description}`;
    }

}
